import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { isMobile } from 'react-device-detect';

import { Box, IconButton, InputAdornment, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from '@features/i18n/infra/useTranslation';
import { usePasswordRules } from 'helpers/validations/rules/usePasswordRules';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { ChangePasswordForm } from '@features/auth/domain/ChangePasswordForm';
import useChangePasswordMutation from '@features/auth/ui/ForgotPassword/hooks/useChangePasswordMutation';
import { LoadingButton, Spacer } from '../../../shared';
import { Typography } from '../../../../src/design-system/atoms';
import PasswordChangedSuccessfully from './PasswordChangedSuccessfully';

function ChangePassword() {
  const { t } = useTranslation();
  const passwordRules = usePasswordRules();
  const [showPassword, setShowPassword] = useState(false);

  const {
    mutateAsync,
    isSuccess,
    isLoading,
    isError: isSubmitError,
    error: submitError,
  } = useChangePasswordMutation();

  const onSubmit = async (data: ChangePasswordForm) => {
    await mutateAsync(data).catch(() => {});
  };

  const { control, handleSubmit } = useForm<ChangePasswordForm>({
    mode: 'onChange',
  });

  if (isSuccess) {
    return <PasswordChangedSuccessfully />;
  }

  return (
    <div>
      <Spacer spacing={isMobile ? 8 : 12} />
      <Typography component="div" variant="h3" align="center">
        <Box fontWeight="fontWeightMedium">Cambio de contraseña</Box>
      </Typography>
      <Spacer spacing={2} />
      <Typography variant="body2" color="textSecondary" align="center">
        Completa este formulario para cambiar tu contraseña
      </Typography>
      <Spacer spacing={isMobile ? 4 : 5} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="newPassword"
          rules={passwordRules}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              variant="outlined"
              label={t('LABEL_NEW_PASSWORD')}
              value={field.value || ''}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              error={
                (fieldState.isTouched || !!isSubmitError) && !!fieldState.error
              }
              helperText={
                (fieldState.isTouched || isSubmitError) &&
                (fieldState.error?.message ||
                  (fieldState.error?.type === 'validate'
                    ? t('ERROR_INVALID_PASSWORD')
                    : ''))
              }
              autoFocus={!isMobile}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('HELPER_PASSWORD_VISIBILITY_ICON')}
                      edge="end"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        {isSubmitError && (
          <>
            <Spacer spacing={2} />
            <Alert severity="error">{t(submitError.message)}</Alert>
          </>
        )}
        <Spacer spacing={3} />
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          isLoading={isLoading}
        >
          Cambiar contraseña
        </LoadingButton>
      </form>

      <Spacer spacing={isMobile ? 2 : 3} />
    </div>
  );
}

export default ChangePassword;
